/**
 * Sign in (Reppr Elite access shell, phase 7).
 *
 * Password sign-in and Google OAuth (with the fresh-signup legitimacy check),
 * session-expired messaging and the returning-user greeting. Users without an
 * agency land on /onboarding rather than being logged back out — see
 * destinationFor below.
 */

import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { login, loginWithGoogle, loginWithMicrosoft, listAuthProviders, pb, logout } from '../lib/pocketbase'
import { joinByDomain, TeamSignInError } from '../api/agencies'
import { PENDING_INVITE_KEY } from './Onboarding'
import { useSession } from '../hooks/useSession'
import AccessShell, { AccessField, AccessCtas, AccessAlt, AccessFoot } from '../components/access/AccessShell'
import styles from '../components/access/AccessShell.module.css'

const HAS_LOGGED_IN_KEY = 'dealpoint_has_logged_in'

/**
 * Athletes authenticate through the separate `client_users` collection, so any
 * record in `users` is agency staff by construction. A staff account with no
 * agency is simply not onboarded yet — the router sends it to /onboarding.
 * (The old gate here logged such users out with a "use the portal" message,
 * which permanently locked out anyone who signed out before finishing
 * onboarding — including invitees.)
 */
function destinationFor(user: any): string {
  if (user?.agency_id || user?.role === 'superadmin') return '/dashboard'
  // An invite link stashed its code before sending the person here to sign in.
  const invite = sessionStorage.getItem(PENDING_INVITE_KEY)
  return invite ? `/onboarding?invite=${encodeURIComponent(invite)}` : '/onboarding'
}

async function isLegitimateUser(user: any): Promise<boolean> {
  if (user.role === 'superadmin') return true
  if (user.agency_id) return true
  if (user.plan && user.plan !== 'free') return true
  // Users older than five minutes are returning users, not fresh signups.
  const createdAt = new Date(user.created)
  const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000)
  if (createdAt < fiveMinutesAgo) return true
  return false
}

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<ReactNode | null>(null)
  const [loading, setLoading] = useState(false)
  const [oauthLoading, setOauthLoading] = useState<'google' | 'microsoft' | null>(null)
  // Which providers this instance actually has configured. Null while asking;
  // a button for an unconfigured provider would open a popup that comes back
  // "provider not configured".
  const [providers, setProviders] = useState<('google' | 'microsoft')[] | null>(null)
  const [providersError, setProvidersError] = useState<string | null>(null)
  useEffect(() => {
    let active = true
    listAuthProviders()
      .then(p => { if (active) setProviders(p) })
      .catch(err => { if (active) { setProviders([]); setProvidersError((err as Error).message) } })
    return () => { active = false }
  }, [])
  const [isReturningUser, setIsReturningUser] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useSession()
  // While an OAuth sign-in is still deciding where to send the user (the
  // team sign-in join runs after the popup closes), the session effect below
  // must not race ahead to /onboarding.
  const oauthInFlight = useRef(false)

  useEffect(() => {
    const hasLoggedIn = localStorage.getItem(HAS_LOGGED_IN_KEY)
    setIsReturningUser(hasLoggedIn === 'true')
    if (searchParams.get('expired') === 'true') {
      setError('Your session has expired. Please sign in again.')
    }
    // Invitation links land here with the code. Keep it for after sign-in;
    // destinationFor reads it back and sends the person to onboarding.
    const invite = searchParams.get('invite')
    if (invite && invite !== '1') sessionStorage.setItem(PENDING_INVITE_KEY, invite)
  }, [searchParams])

  useEffect(() => {
    if (user && !oauthInFlight.current) {
      localStorage.setItem(HAS_LOGGED_IN_KEY, 'true')
      navigate(destinationFor(user))
    }
  }, [navigate, user])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await login(email, password)
      localStorage.setItem(HAS_LOGGED_IN_KEY, 'true')
      navigate(destinationFor(pb.authStore.model))
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Team sign-in. A staff account with no agency may belong to one by email
   * domain; /api/join-by-domain decides that, server-side, from the OAuth link
   * PocketBase recorded. Returns true when the user was attached.
   *
   * This runs BEFORE isLegitimateUser: a brand-new OAuth account has no agency
   * and no plan, and that check would log it out and send it to pricing before
   * it could join.
   */
  async function tryJoinByDomain(): Promise<boolean> {
    const current = pb.authStore.model as any
    if (!current || current.agency_id || current.role === 'superadmin') return false
    try {
      await joinByDomain()
    } catch (err) {
      if (err instanceof TeamSignInError &&
          (err.code === 'NO_MATCHING_DOMAIN' || err.code === 'NOT_OAUTH_VERIFIED')) {
        return false
      }
      throw err
    }
    // agency_id is hidden from getOne and only comes back on an auth response,
    // so fetchFreshUser cannot see the change. A one-off authRefresh can.
    await pb.collection('users').authRefresh()
    return true
  }

  async function finishOAuthLogin(provider: 'google' | 'microsoft', signIn: () => Promise<unknown>, providerLabel: string) {
    setError(null)
    setOauthLoading(provider)
    oauthInFlight.current = true
    try {
      await signIn()
      if (await tryJoinByDomain()) {
        localStorage.setItem(HAS_LOGGED_IN_KEY, 'true')
        navigate('/dashboard')
        return
      }
      const currentUser = pb.authStore.model
      if (currentUser) {
        if (!(await isLegitimateUser(currentUser))) {
          await logout()
          // /#pricing is not an anchor on the landing page, so this put people
          // at the top of the homepage with no explanation. /pricing is real,
          // says what the tiers are, and offers the walkthrough.
          window.location.href = '/pricing'
          return
        }
      }
      localStorage.setItem(HAS_LOGGED_IN_KEY, 'true')
      navigate(destinationFor(pb.authStore.model))
    } catch (err: any) {
      if (err instanceof TeamSignInError) {
        // The join was refused for a reason the user has to act on (seats
        // full, agency inactive). Signing them in anyway would strand them on
        // onboarding with no explanation, so stop here and say why.
        await logout()
        setError(err.code === 'SEATS_FULL'
          ? 'Your company’s workspace is full. Ask your admin to add a seat, then sign in again.'
          : err.message)
        return
      }
      if (err.message?.includes('cancelled') || err.message?.includes('closed')) {
        // User closed the popup, not an error.
      } else {
        setError(err.message || `${providerLabel} sign-in failed`)
      }
    } finally {
      oauthInFlight.current = false
      setOauthLoading(null)
    }
  }

  function handleGoogleLogin() {
    return finishOAuthLogin('google', loginWithGoogle, 'Google')
  }

  function handleMicrosoftLogin() {
    return finishOAuthLogin('microsoft', loginWithMicrosoft, 'Microsoft')
  }

  return (
    <AccessShell
      eyebrow="Sign in"
      title={isReturningUser ? 'Welcome back.' : 'Sign in.'}
      blurb={searchParams.get('invite')
        ? 'You have an invitation waiting. Sign in on the invited address and you will be added to the agency. No account yet? Create one with your invitation below.'
        : 'Your desk is where you left it.'}
      width={440}
    >
      <form onSubmit={handleSubmit} noValidate>
        <AccessField label="Work email" htmlFor="email">
          <input
            id="email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="name@agency.com"
            autoComplete="email"
            required
          />
        </AccessField>

        <AccessField
          label="Password"
          htmlFor="password"
          aide={<Link to="/forgot-password">Forgot password?</Link>}
        >
          <input
            id="password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="••••••••••••"
            autoComplete="current-password"
            required
          />
        </AccessField>

        {error && <div className={styles.error}>{error}</div>}

        <AccessCtas
          primary={loading ? 'Signing in…' : 'Sign in'}
          disabled={loading || oauthLoading !== null}
        />
      </form>

      {(providers === null || providers.length > 0) && <div className={styles.divider}>or</div>}
      {providersError && (
        <div className={styles.error}>
          Could not check which single sign-on options are available: {providersError}. Sign in with your password above.
        </div>
      )}

      {providers?.includes('google') && <button
        type="button"
        onClick={handleGoogleLogin}
        disabled={loading || oauthLoading !== null}
        className={styles.oauthBtn}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
          <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
          <path d="M9.003 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332A8.997 8.997 0 0 0 9.003 18z" fill="#34A853"/>
          <path d="M3.964 10.712A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.96A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.96 4.042l3.004-2.33z" fill="#FBBC05"/>
          <path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.002 0A8.997 8.997 0 0 0 .96 4.958l3.004 2.332c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335"/>
        </svg>
        {oauthLoading === 'google' ? 'Signing in…' : 'Continue with Google'}
      </button>}

      {providers?.includes('microsoft') && <button
        type="button"
        onClick={handleMicrosoftLogin}
        disabled={loading || oauthLoading !== null}
        className={styles.oauthBtn}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <rect x="0" y="0" width="8.5" height="8.5" fill="#F25022"/>
          <rect x="9.5" y="0" width="8.5" height="8.5" fill="#7FBA00"/>
          <rect x="0" y="9.5" width="8.5" height="8.5" fill="#00A4EF"/>
          <rect x="9.5" y="9.5" width="8.5" height="8.5" fill="#FFB900"/>
        </svg>
        {oauthLoading === 'microsoft' ? 'Signing in…' : 'Continue with Microsoft'}
      </button>}

      <AccessAlt prompt="No account yet?">
        {searchParams.get('invite') && searchParams.get('invite') !== '1'
          ? <Link to={`/register?invite=${encodeURIComponent(searchParams.get('invite') ?? '')}`}>Create your account with this invitation</Link>
          : <Link to="/register">Request agency access</Link>}
      </AccessAlt>

      <AccessFoot>
        Athletes: sign in at the <Link to="/portal/login">client portal</Link> with the link your
        agency sent. <Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link>.
      </AccessFoot>
    </AccessShell>
  )
}
